/**
 * Globally mounted source of relay-held Polymarket positions and resting
 * orders for the account plane (feed-filters-per-view, Danielle round 1).
 * Mounted once in the authenticated shell beside `FeedNeedsRouteFallback`,
 * so the held identities are declared on EVERY route — not only while a
 * position-aware view happens to be open.
 *
 * Reuses the two relay hooks verbatim (identity-keyed state, retained rows
 * through transient failures, debounced unavailable, confirmed empty) and
 * mirrors their results into `polyRelayAccountStore`, which applies only
 * `ready` results and resets on an identity change. Inert when the consumer
 * feed filters are off: no fetch, no poll, nothing mirrored — the lane's
 * relay traffic is unchanged.
 *
 * Cost: one `/api/polymarket/positions` poll (10 s, no P&L) per browser tab;
 * the orders fetch is module-shared with every other mount.
 */
import { useEffect, useMemo } from 'react';
import { feedFiltersEnabled } from '@/clients/feedFilterConfig';
import { useAuthStore } from '@/stores/authStore';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import { usePolymarketRelayOrders } from '@/hooks/usePolymarketRelayOrders';
import { usePolyRelayAccountStore } from '@/stores/polyRelayAccountStore';

export function AccountFeedNeedsBridge() {
  // Read once per mount, like the fleet (the flag is a page-load decision).
  const enabled = useMemo(() => feedFiltersEnabled(), []);
  // The same composed identity both hooks stamp their state with.
  const identity = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const positions = usePolymarketRelayPositions({ enabled, includePnl: false });
  const orders = usePolymarketRelayOrders({ enabled });
  useEffect(() => {
    if (!enabled) return;
    usePolyRelayAccountStore.getState().applyPositions(identity, positions.status, positions.positions);
  }, [enabled, identity, positions.status, positions.positions]);
  useEffect(() => {
    if (!enabled) return;
    usePolyRelayAccountStore.getState().applyOrders(identity, orders.status, orders.orders);
  }, [enabled, identity, orders.status, orders.orders]);
  return null;
}
